const TOKEN_G = () => /(\{\{\{?)\s*([^{}\s]+)([^{}]*)(\}?\}\})/g;

/**
 * Apply `fn` to every string in a settings value, recursing into arrays and
 * plain objects — a table keeps its cell text in `settings.tableData[].content`,
 * not in a top-level string. Returns the SAME reference when nothing changed,
 * so `diffChangedSettings` only patches the props that actually moved.
 */
export const mapSettingsStrings = (
  value: unknown,
  fn: (s: string) => string,
): unknown => {
  if (typeof value === "string") return fn(value);
  if (!value || typeof value !== "object") return value;
  let changed = false;
  const visit = (v: unknown): unknown => {
    const next = mapSettingsStrings(v, fn);
    if (next !== v) changed = true;
    return next;
  };
  const out = Array.isArray(value)
    ? value.map(visit)
    : Object.fromEntries(Object.entries(value).map(([k, v]) => [k, visit(v)]));
  return changed ? out : value;
};

const escapeRe = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Rewrite `{{name}}` / `{{{name}}}` → `{{key.name}}` / `{{{key.name}}}` for every
 * `name` in `fieldNames`, across all string-valued settings. Non-matching tokens
 * and non-string settings are left untouched.
 */
export const wrapSettingsTokens = (
  settings: Record<string, unknown>,
  key: string,
  fieldNames: Set<string>,
): Record<string, unknown> => {
  const next: Record<string, unknown> = { ...settings };
  for (const [prop, value] of Object.entries(settings)) {
    next[prop] = mapSettingsStrings(value, (str) =>
      str.replace(TOKEN_G(), (full, open, rawName, rest, close) => {
        const name = String(rawName);
        // Defense-in-depth double-wrap guard: never namespace a token that
        // is already namespaced/global (contains a dot), even if a caller's
        // fieldNames set included it by mistake — `{{inner.field}}` must
        // never become `{{outer.inner.field}}`. planRepeatable already
        // excludes dotted names from fieldNames (see derive-fields.ts), so
        // this is a second, independent guard at the wrap step itself.
        if (name.includes(".")) return full;
        if (!fieldNames.has(name)) return full;
        return `${open}${key}.${name}${rest}${close}`;
      }),
    );
  }
  return next;
};

/** Reverse of wrapSettingsTokens for a single key: `{{key.name}}` → `{{name}}`. */
export const unwrapSettingsTokens = (
  settings: Record<string, unknown>,
  key: string,
): Record<string, unknown> => {
  const prefix = escapeRe(key + ".");
  const re = new RegExp(`(\\{\\{\\{?)\\s*${prefix}([^{}\\s.]+)`, "g");
  const next: Record<string, unknown> = { ...settings };
  for (const [prop, value] of Object.entries(settings)) {
    next[prop] = mapSettingsStrings(value, (str) =>
      str.replace(re, (_full, open, field) => `${open}${field}`),
    );
  }
  return next;
};

/**
 * Move tokens from one group's namespace to another's:
 * `{{from.field}}` → `{{to.field}}`, or → `{{field}}` when `toKey` is null.
 *
 * A token always belongs to its NEAREST enclosing repeatable group, so marking
 * a nested group re-parks the outer group's tokens down into it, and un-marking
 * re-parks them back up to the next group out (or to bare when there is none).
 * Only the exact `from` prefix is rewritten — other groups' namespaces and
 * global dotted tokens (`hubspot.contact.email`) are left alone.
 */
export const reparentSettingsTokens = (
  settings: Record<string, unknown>,
  fromKey: string,
  toKey: string | null,
): Record<string, unknown> => {
  const prefix = escapeRe(`${fromKey}.`);
  const re = new RegExp(`(\\{\\{\\{?)\\s*${prefix}([^{}\\s.]+)`, "g");
  const next: Record<string, unknown> = { ...settings };
  for (const [prop, value] of Object.entries(settings)) {
    next[prop] = mapSettingsStrings(value, (str) =>
      str.replace(re, (_full, open, field) =>
        toKey ? `${open}${toKey}.${field}` : `${open}${field}`,
      ),
    );
  }
  return next;
};

/**
 * Field keys inside `settings` that are namespaced to `key`
 * (`{{key.field}}` → "field"). Used to work out which of an ancestor group's
 * fields a newly marked nested group takes over.
 */
export const collectNamespacedFieldKeys = (
  settings: Record<string, unknown>,
  key: string,
): string[] => {
  const prefix = escapeRe(`${key}.`);
  const re = new RegExp(`\\{\\{\\{?\\s*${prefix}([^{}\\s.]+)`, "g");
  const found = new Set<string>();
  mapSettingsStrings(settings, (str) => {
    let match: RegExpExecArray | null = re.exec(str);
    while (match !== null) {
      found.add(match[1]);
      match = re.exec(str);
    }
    return str;
  });
  return Array.from(found);
};
